/**
 * Sunday Clovr — motion foundation. MINIMAL, deliberately.
 *
 * History: this file went through five rounds of patches — Lenis on/off,
 * scroller proxies registered and removed, manual update drivers, scroll
 * memory clears. The result was ScrollTrigger latching onto a dead scroll
 * getter: it reported position 0 while the browser was at 3000, so every
 * scrubbed animation sat at progress 0 forever.
 *
 * The fix is the absence of cleverness. The document scrolls (sc-motion.css
 * overrides Horizon's .page-wrapper scroller), and ScrollTrigger installs
 * its OWN native scroll listeners — it needs no proxy, no defaults, no
 * external update driver, no smoothing library. Do not add any of those
 * back without a measured reason.
 */

const REDUCED = '(prefers-reduced-motion: reduce)';
const prefersReduced = () => matchMedia(REDUCED).matches;
const CONSUMER_SELECTOR = [
  '[data-sc-hero]',
  '[data-sc-dive]',
  '[data-sc-editorial]',
  '[data-sc-orbital]',
  '[data-sc-finale]',
].join(', ');

let gsap, ScrollTrigger;
let booted = false;
let savedScroll = 0;
const cleanups = new Set();

export const Motion = {
  get gsap() { return gsap; },
  get ScrollTrigger() { return ScrollTrigger; },
  get reduced() { return prefersReduced(); },

  /** Overlays: lock scroll, remember position. */
  pause() {
    savedScroll = scrollY;
    document.documentElement.setAttribute('scroll-lock', '');
  },
  resume() {
    document.documentElement.removeAttribute('scroll-lock');
    scrollTo(0, savedScroll);
  },

  refresh() { ScrollTrigger?.refresh(); },

  scrollTo(y) { scrollTo({ top: y, behavior: 'auto' }); },

  onCleanup(fn) { cleanups.add(fn); },
};

// Exposed synchronously: overlays call pause()/resume() on the first click, which can
// happen before (or without) the GSAP imports below. Readiness (`sc:motion:ready`,
// `SCMotion.ScrollTrigger`) is still signalled only after the imports resolve.
window.SCMotion = Motion;

function killAll() {
  cleanups.forEach((fn) => { try { fn(); } catch (e) { console.error('[sc-motion]', e); } });
  cleanups.clear();
  ScrollTrigger?.getAll().forEach((t) => t.kill());
}

async function boot() {
  if (booted) return;
  booted = true;

  const [g, st] = await Promise.all([
    import('https://cdn.jsdelivr.net/npm/gsap@3.13.0/index.js'),
    import('https://cdn.jsdelivr.net/npm/gsap@3.13.0/ScrollTrigger.js'),
  ]);
  gsap = g.gsap || g.default;
  ScrollTrigger = st.ScrollTrigger || st.default;
  gsap.registerPlugin(ScrollTrigger);
  // iOS fires resize whenever the URL bar collapses DURING scrolling; a
  // full refresh mid-scroll is exactly the stutter the client felt on
  // the phone. Ignore those height-only mobile resizes entirely.
  ScrollTrigger.config({ ignoreMobileResize: true });

  document.dispatchEvent(new CustomEvent('sc:motion:ready', { detail: { reduced: prefersReduced() } }));

  // Measurements settle late; refresh at the moments that change them.
  //
  // THE bug that froze every animation for two sessions lived here: boot()
  // resolves after its CDN imports, which is usually AFTER window 'load' has
  // already fired — so a load listener registered now never runs, and the
  // triggers keep the garbage measurements they were born with. A fresh
  // trigger created later worked instantly and one manual refresh() revived
  // all of them, which is how this was finally isolated.
  const settle = () => ScrollTrigger.refresh();
  document.fonts?.ready.then(settle);
  if (document.readyState === 'complete') settle();
  else addEventListener('load', settle, { once: true });
  // Belt and braces: images and pin-spacers keep shifting layout briefly.
  setTimeout(settle, 800);
  setTimeout(settle, 2500);

  // Drawers (cart, search dialogs) squeeze the page while open. Pinned
  // sections snapshot their width when pinning, so without a refresh
  // the hero keeps the squeezed width after the drawer closes and a
  // dead strip is left down one side. Re-measure on every open/close.
  let drawerT;
  const settleAfterDrawer = () => {
    clearTimeout(drawerT);
    drawerT = setTimeout(settle, 420);
  };
  // The cart drawer is injected into the DOM lazily (after the first
  // add-to-cart), so per-element observers attached at load watched
  // nothing. One document-wide observer catches every open/close of
  // any drawer or dialog, whenever it appears.
  const watchDrawers = () => {
    new MutationObserver((muts) => {
      for (const m of muts) {
        const t = m.target;
        if (t.tagName === 'DIALOG' || t.tagName === 'CART-DRAWER') {
          settleAfterDrawer();
          return;
        }
      }
    }).observe(document.documentElement, {
      subtree: true,
      attributes: true,
      attributeFilter: ['open'],
    });
  };
  if (document.readyState !== 'loading') watchDrawers();
  else addEventListener('DOMContentLoaded', watchDrawers, { once: true });
  // Same idea for our own handler: only a WIDTH change is a real resize.
  // Height changes on phones are just the browser chrome sliding away.
  let t;
  let lastW = innerWidth;
  addEventListener('resize', () => {
    if (innerWidth === lastW) return;
    lastW = innerWidth;
    clearTimeout(t);
    t = setTimeout(() => ScrollTrigger.refresh(), 150);
  }, { passive: true });

  // Shopify design mode: sections are swapped under us. Sections re-mount
  // themselves on section:load; the foundation just refreshes.
  document.addEventListener('shopify:section:load', () => ScrollTrigger.refresh());
  document.addEventListener('shopify:section:unload', () => ScrollTrigger.refresh());

  // Horizon navigates with cross-document view transitions; leave nothing behind.
  addEventListener('pagehide', killAll);
}

function hasMotionConsumer(root = document) {
  return Boolean(root.matches?.(CONSUMER_SELECTOR) || root.querySelector?.(CONSUMER_SELECTOR));
}

function bootIfNeeded(root = document) {
  if (hasMotionConsumer(root)) boot();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => bootIfNeeded(), { once: true });
} else {
  bootIfNeeded();
}

// Theme Editor sections arrive after the initial document. Only a section
// that actually contains a GSAP consumer is allowed to start the engine.
document.addEventListener('shopify:section:load', (event) => bootIfNeeded(event.target));
